import { useCallback, useEffect, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';

import { useImmediateEffect } from '@libs/hooks/useImmediateEffect';

import { useClubCardQuery } from '@features/club/api/hooks';
import { ParticipantStatus } from '@features/shared/api/typings';
import {
  QUERY_KEYS,
  useClubMembersForTournamentModalQuery,
  useTournamentParticipantStatusMutation,
} from '@features/tournament/api/hooks';
import { ParticipantsTab } from '@features/tournament/ui/components/TournamentParticipantsEdit/types';

type Sort = 'AlphabetAsc' | 'RatingDesc';

export function useParticipantsModal({
  clubId,
  tournamentId,
  tab,
  disabled = false,
}: {
  clubId: string;
  tournamentId: string;
  tab: ParticipantsTab;
  disabled?: boolean;
}) {
  const [sort, setSort] = useState<Sort>('AlphabetAsc');
  const [search, setSearch] = useState('');
  const { data: club } = useClubCardQuery(clubId, {
    staleTime: Infinity,
  });

  const { mutateAsync: updateUser } = useTournamentParticipantStatusMutation(clubId, tournamentId);

  // Инвалидация запросов
  const ql = useQueryClient();
  useImmediateEffect(() => {
    if (disabled) {
      ql.invalidateQueries({
        queryKey: QUERY_KEYS.tournamentParticipants(clubId, tournamentId),
      });
    }
  }, [clubId, disabled, ql, tournamentId]);

  const {
    data: members,
    hasNextPage,
    fetchNextPage,
  } = useClubMembersForTournamentModalQuery(clubId, tournamentId, {
    order: sort,
    status: 'Active',
    query: search,
    disabled,
  });

  const onInvite = useCallback(
    async (p: { userId: string; participationStatus: ParticipantStatus }) => {
      const result = await updateUser({ userId: p.userId, action: 'invite' });
      // FIXME: патчить чужой стейт
      if (result!) p.participationStatus = 'Invited';
    },
    [updateUser],
  );

  const onAdd = useCallback(
    async (p: { userId: string; participationStatus: ParticipantStatus }) => {
      const result = await updateUser({ userId: p.userId, action: 'add' });
      // FIXME: патчить чужой стейт
      if (result!) p.participationStatus = 'Participating';
    },
    [updateUser],
  );

  const onDelete = useCallback(
    async (p: { userId: string; participationStatus: ParticipantStatus }) => {
      await updateUser({ userId: p.userId, action: 'delete' });
      // FIXME: патчить чужой стейт
      // FIXME: попросить бэк возвращать юзера, как в других запросах
      p.participationStatus = 'Exited';
    },
    [updateUser],
  );

  // При переоткрытия модалки нужно сбрасывать поиск
  useEffect(() => {
    setSearch('');
  }, [disabled]);

  return {
    sort,
    onSortChange: setSort,
    search,
    onSearch: setSearch,
    members,
    hasNextPage,
    fetchNextPage,
    onAdd: tab === 'Participating' ? onAdd : undefined,
    onInvite: tab === 'Invited' ? onInvite : undefined,
    onDelete,
    groupTitle: club?.groupTitle,
  };
}
